import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, Ticket, ArrowUpRight, Mic2, CalendarPlus, Video, Lock } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import SectionLabel from "@/components/SectionLabel";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const TYPE_LABEL = {
  show: "Live Show",
  keynote: "Keynote",
  festival: "Festival",
  appearance: "Appearance",
  studio: "Studio Session",
  tour: "Tour Date",
  backstage: "Backstage · Live",
};

function formatEventDate(iso, time) {
  if (!iso) return "TBA";
  // Allow free-form like "TBA" or "Summer 2026"
  if (!/^\d{4}-\d{2}-\d{2}/.test(iso)) return iso;
  try {
    const d = new Date(iso);
    const dateStr = d.toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    return time ? `${dateStr} · ${time}` : dateStr;
  } catch {
    return iso;
  }
}

// ---- Calendar deep-link builders ----
function parseEventDateTime(dateIso, timeStr) {
  // returns { start: Date, end: Date, allDay: bool } or null
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateIso || "")) return null;
  const [y, m, d] = dateIso.split("-").map(Number);
  const base = new Date(y, m - 1, d, 0, 0, 0);
  if (timeStr) {
    const tm = /^(\d{1,2}):(\d{2})\s*(AM|PM)?/i.exec(timeStr.trim());
    if (tm) {
      let hh = parseInt(tm[1], 10);
      const mm = parseInt(tm[2], 10);
      const mer = (tm[3] || "").toUpperCase();
      if (mer === "PM" && hh < 12) hh += 12;
      if (mer === "AM" && hh === 12) hh = 0;
      const start = new Date(y, m - 1, d, hh, mm, 0);
      const end = new Date(start.getTime() + 2 * 60 * 60 * 1000); // 2 hours
      return { start, end, allDay: false };
    }
  }
  const end = new Date(y, m - 1, d + 1);
  return { start: base, end, allDay: true };
}

function pad(n) {
  return n.toString().padStart(2, "0");
}
function toGCalDateTime(d, allDay) {
  if (allDay) {
    return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
  }
  // Use floating local time
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}T${pad(d.getHours())}${pad(d.getMinutes())}00`;
}
function toOutlookISO(d) {
  // Outlook accepts ISO without zone — treats as local
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
}

function buildCalendarLinks(event) {
  const dt = parseEventDateTime(event.date, event.time);
  if (!dt) return null;
  const location = [event.venue, event.city].filter(Boolean).join(" · ");
  let body = event.description || "";
  if (event.ticket_url) body += `\n\nTickets: ${event.ticket_url}`;
  body += "\n\nFrom Nina Capone — ninacapone.com";
  const enc = encodeURIComponent;

  const gcalDates = `${toGCalDateTime(dt.start, dt.allDay)}/${toGCalDateTime(dt.end, dt.allDay)}`;
  const google =
    `https://calendar.google.com/calendar/render?action=TEMPLATE` +
    `&text=${enc(event.title)}` +
    `&dates=${gcalDates}` +
    `&details=${enc(body)}` +
    `&location=${enc(location)}`;

  const outlook =
    `https://outlook.live.com/calendar/0/deeplink/compose?path=/calendar/action/compose&rru=addevent` +
    `&subject=${enc(event.title)}` +
    `&body=${enc(body)}` +
    `&location=${enc(location)}` +
    `&startdt=${enc(toOutlookISO(dt.start))}` +
    `&enddt=${enc(toOutlookISO(dt.end))}` +
    (dt.allDay ? "&allday=true" : "");

  // .ics (Apple / Outlook desktop / iCal) is served by the backend
  const origin =
    typeof window !== "undefined" ? window.location.origin : "";
  const ics = `${origin}/api/public/events/${event.id}/ics`;

  return { google, outlook, ics };
}

function AddToCalendar({ event }) {
  const links = buildCalendarLinks(event);
  if (!links) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-white/65 hover:text-[color:var(--gold)] transition-colors"
          data-testid={`event-add-cal-${event.id}`}
          aria-label="Add to calendar"
        >
          <CalendarPlus size={12} />
          Add To Calendar
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={6}
        className="bg-[#0a050d] border-white/10 text-white/85 min-w-[200px]"
      >
        <DropdownMenuItem
          asChild
          className="cursor-pointer text-xs uppercase tracking-[0.2em] focus:bg-[color:var(--gold)]/10 focus:text-[color:var(--gold)]"
        >
          <a
            href={links.google}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`event-cal-google-${event.id}`}
          >
            Google Calendar
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem
          asChild
          className="cursor-pointer text-xs uppercase tracking-[0.2em] focus:bg-[color:var(--gold)]/10 focus:text-[color:var(--gold)]"
        >
          <a
            href={links.ics}
            data-testid={`event-cal-ics-${event.id}`}
          >
            Apple / iCal (.ics)
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem
          asChild
          className="cursor-pointer text-xs uppercase tracking-[0.2em] focus:bg-[color:var(--gold)]/10 focus:text-[color:var(--gold)]"
        >
          <a
            href={links.outlook}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`event-cal-outlook-${event.id}`}
          >
            Outlook
          </a>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function EventCard({ event, compact = false }) {
  const isFeatured = event.featured;
  const typeLabel = TYPE_LABEL[event.event_type] || "Event";
  const isBackstage = event.event_type === "backstage";

  return (
    <article
      className={`surface relative overflow-hidden group transition-all duration-300 ${
        compact ? "p-6" : "p-7 lg:p-8"
      } ${isFeatured || isBackstage ? "border-[color:var(--gold)]/30" : ""}`}
      data-testid={`event-card-${event.id}`}
    >
      {(isFeatured || isBackstage) && (
        <div
          className="absolute top-0 right-0 w-44 h-44 rounded-full opacity-15 blur-3xl pointer-events-none"
          style={{ background: "var(--gold)" }}
        />
      )}
      <div className="relative space-y-4">
        {event.flyer_url && (
          <div
            className={`relative overflow-hidden mb-1 ${compact ? "-mx-6 -mt-6" : "-mx-7 -mt-7 lg:-mx-8 lg:-mt-8"}`}
            data-testid={`event-flyer-${event.id}`}
          >
            <img
              src={imgUrl(event.flyer_url)}
              alt={`${event.title} flyer`}
              loading="lazy"
              className="w-full h-48 object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--surface)] via-transparent to-transparent pointer-events-none" />
          </div>
        )}
        <div className="flex items-start justify-between gap-3">
          <span
            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em]"
            style={{ color: "var(--gold)" }}
          >
            <Mic2 size={11} /> {typeLabel}
          </span>
          {isBackstage ? (
            <span
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] px-2 py-1 rounded-full border"
              style={{ color: "var(--gold)", borderColor: "rgba(232,180,184,0.5)" }}
              data-testid={`event-members-pill-${event.id}`}
            >
              <Lock size={10} /> Members Only
            </span>
          ) : event.sold_out ? (
            <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 border border-red-400/40 text-red-300/90 rounded-full">
              Sold Out
            </span>
          ) : (
            event.status === "cancelled" && (
              <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 border border-white/15 text-white/45 rounded-full">
                Cancelled
              </span>
            )
          )}
        </div>

        <h3 className="font-serif font-light tracking-tight text-2xl lg:text-3xl leading-tight group-hover:text-[color:var(--gold)] transition-colors">
          {event.title}
        </h3>

        <div className="space-y-2 text-sm text-white/70">
          <div className="flex items-center gap-2.5">
            <Calendar size={13} className="opacity-70" />
            <span>{formatEventDate(event.date, event.time)}</span>
          </div>
          {(event.venue || event.city) && (
            <div className="flex items-center gap-2.5">
              <MapPin size={13} className="opacity-70" />
              <span>
                {[event.venue, event.city].filter(Boolean).join(" · ")}
              </span>
            </div>
          )}
        </div>

        {event.description && !compact && (
          <p className="text-sm text-white/55 leading-relaxed line-clamp-3">
            {event.description}
          </p>
        )}

        <div className="flex items-center justify-between gap-3 pt-2 flex-wrap">
          {isBackstage && event.status !== "cancelled" ? (
            <Link
              to="/backstage"
              className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] group/cta"
              style={{ color: "var(--gold)" }}
              data-testid={`event-backstage-join-${event.id}`}
            >
              <Video size={13} />
              Join Live · Be.Live
              <ArrowUpRight
                size={12}
                className="transition-transform group-hover/cta:translate-x-0.5 group-hover/cta:-translate-y-0.5"
              />
            </Link>
          ) : event.ticket_url && event.status !== "cancelled" && !event.sold_out ? (
            <a
              href={event.ticket_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] group/cta"
              style={{ color: "var(--gold)" }}
              data-testid={`event-ticket-${event.id}`}
            >
              <Ticket size={13} />
              Get Tickets
              <ArrowUpRight
                size={12}
                className="transition-transform group-hover/cta:translate-x-0.5 group-hover/cta:-translate-y-0.5"
              />
            </a>
          ) : (
            <span />
          )}
          {event.status !== "cancelled" && <AddToCalendar event={event} />}
        </div>
      </div>
    </article>
  );
}

/**
 * UpcomingShows — public-facing events list.
 *
 * Props:
 *  - heading: title shown above (default "Upcoming Shows & Appearances")
 *  - limit: max events to show (default 6)
 *  - compact: smaller card variant
 *  - showLabel: section eyebrow label (default "Live · On The Calendar")
 *  - hideWhenEmpty: if true, render nothing when list is empty (default false)
 */
export default function UpcomingShows({
  heading = "Upcoming Shows & Appearances",
  limit = 6,
  compact = false,
  showLabel = "Live · On The Calendar",
  hideWhenEmpty = false,
  className = "",
}) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/public/events", { params: { limit } })
      .then((r) => setEvents(r.data.events || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [limit]);

  if (loading) return null;
  if (!events.length && hideWhenEmpty) return null;

  return (
    <section
      className={`relative py-24 border-t border-white/5 ${className}`}
      data-testid="upcoming-shows"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div className="space-y-3 max-w-2xl">
            <SectionLabel label={showLabel} />
            <h2 className="font-serif font-light tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              {heading.split(" ").slice(0, -1).join(" ")}{" "}
              <em className="italic" style={{ color: "var(--gold)" }}>
                {heading.split(" ").slice(-1)[0]}
              </em>
            </h2>
          </div>
          <p className="text-xs uppercase tracking-[0.25em] text-white/45">
            {events.length} on the calendar
          </p>
        </div>

        {events.length === 0 ? (
          <div className="surface p-14 text-center">
            <Calendar
              size={36}
              strokeWidth={1}
              style={{ color: "var(--gold)" }}
              className="mx-auto mb-4 opacity-60"
            />
            <p className="font-serif text-2xl text-white/75 mb-2">
              No upcoming dates announced{" "}
              <em className="italic" style={{ color: "var(--gold)" }}>
                yet.
              </em>
            </p>
            <p className="text-sm text-white/45 max-w-md mx-auto">
              Subscribe to the Inner Circle and you&rsquo;ll be the first to know
              when the next shows drop.
            </p>
          </div>
        ) : (
          <div
            className={`grid gap-5 lg:gap-6 ${
              compact
                ? "sm:grid-cols-2 lg:grid-cols-3"
                : "md:grid-cols-2 lg:grid-cols-3"
            }`}
          >
            {events.map((e) => (
              <EventCard key={e.id} event={e} compact={compact} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
